import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, ShieldCheckIcon } from '@heroicons/react/24/solid';
import { BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import PublicNavbar from '../common/PublicNavbar';
import PublicFooter from '../common/PublicFooter';
import { Spinner } from 'react-bootstrap';

type VerifyStatus = 'loading' | 'success' | 'already-verified' | 'error';

const VerifyEmail: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<VerifyStatus>('loading');
  const [message, setMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const apiBase = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://127.0.0.1:8001/api'
    : '/api';

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link.');
      return;
    }

    fetch(`${apiBase}/auth/verify-email/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (res.ok && data.code === 'already_verified') {
          setStatus('already-verified');
          setMessage(data.message);
        } else if (res.ok) {
          setStatus('success');
          setMessage(data.message || 'Email verified successfully!');
        } else {
          setStatus('error');
          setMessage(data.error || 'Verification failed.');
        }
      })
      .catch(() => {
        setStatus('error');
        setMessage('Connection error. Please try again.');
      });
  }, [token, apiBase]);

  const handleResend = async () => {
    if (!resendEmail || resendStatus === 'sending') return;
    setResendStatus('sending');
    try {
      await fetch(`${apiBase}/auth/resend-verification/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resendEmail }),
      });
      setResendStatus('sent');
    } catch {
      setResendStatus('error');
    }
  };

  const isSuccess = status === 'success' || status === 'already-verified';

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicNavbar solid />
      <div style={{
        flex: 1,
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #3b82f6 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        paddingTop: '100px', padding: '100px 1rem 3rem',
      }}>
        <div style={{
          background: 'white', borderRadius: '24px', maxWidth: '480px', width: '100%',
          padding: 'clamp(32px, 6vw, 48px)', textAlign: 'center',
          boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
        }}>
          {status === 'loading' ? (
            <>
              <Spinner animation="border" style={{ width: '48px', height: '48px', color: '#1e3a8a', marginBottom: '1.25rem' }} />
              <h3 style={{ fontWeight: 800, color: '#0f172a', fontSize: '1.5rem' }}>Verifying your email...</h3>
              <p style={{ color: '#64748b', marginTop: '0.5rem' }}>Please wait a moment.</p>
            </>
          ) : (
            <>
              <div style={{
                width: '72px', height: '72px', borderRadius: '50%',
                background: status === 'already-verified'
                  ? 'linear-gradient(135deg, #dbeafe, #bfdbfe)'
                  : isSuccess
                    ? 'linear-gradient(135deg, #d1fae5, #a7f3d0)'
                    : 'linear-gradient(135deg, #fee2e2, #fecaca)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 1.25rem',
              }}>
                {status === 'already-verified'
                  ? <ShieldCheckIcon style={{ width: '40px', height: '40px', color: '#2563eb' }} />
                  : isSuccess
                    ? <CheckCircleIcon style={{ width: '40px', height: '40px', color: '#059669' }} />
                    : <XCircleIcon style={{ width: '40px', height: '40px', color: '#dc2626' }} />
                }
              </div>
              <h3 style={{ fontWeight: 800, color: '#0f172a', marginBottom: '0.5rem', fontSize: '1.5rem' }}>
                {status === 'already-verified'
                  ? 'Account already activated'
                  : isSuccess
                    ? 'Email verified!'
                    : 'Verification failed'}
              </h3>
              <p style={{ color: '#64748b', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                {status === 'already-verified'
                  ? 'Your account has already been verified. You can log in to your dashboard.'
                  : isSuccess
                    ? 'Your account is now active. You can log in and start using your dashboard.'
                    : message}
              </p>
              {isSuccess ? (
                <Link
                  to="/customer-login"
                  className="btn d-inline-flex align-items-center gap-2"
                  style={{
                    borderRadius: '12px', padding: '12px 32px', fontWeight: 700, fontSize: '1rem',
                    background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', color: 'white',
                    border: 'none', boxShadow: '0 4px 15px rgba(30,58,138,0.3)', textDecoration: 'none',
                  }}
                >
                  <BuildingStorefrontIcon style={{ width: '20px', height: '20px' }} />
                  Go to Login
                </Link>
              ) : (
                <div className="d-flex flex-column align-items-center gap-2">
                  <Link
                    to="/customer-login"
                    className="btn d-inline-flex align-items-center gap-2"
                    style={{
                      borderRadius: '12px', padding: '12px 32px', fontWeight: 700, fontSize: '1rem',
                      background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', color: 'white',
                      border: 'none', boxShadow: '0 4px 15px rgba(30,58,138,0.3)', textDecoration: 'none',
                    }}
                  >
                    Go to Login
                  </Link>
                  <div style={{ marginTop: '1rem', width: '100%', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
                    <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                      Enter your email to receive a new verification link:
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="email"
                        placeholder="your@email.com"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        style={{
                          flex: 1, padding: '10px 14px', borderRadius: '10px',
                          border: '1px solid #cbd5e1', fontSize: '0.9rem', outline: 'none',
                        }}
                      />
                      <button
                        onClick={handleResend}
                        disabled={!resendEmail || resendStatus === 'sending' || resendStatus === 'sent'}
                        style={{
                          padding: '10px 18px', borderRadius: '10px', border: 'none',
                          background: resendStatus === 'sent' ? '#059669' : '#1e3a8a',
                          color: 'white', fontWeight: 600, fontSize: '0.85rem',
                          cursor: resendStatus === 'sent' ? 'default' : 'pointer',
                          opacity: !resendEmail || resendStatus === 'sending' ? 0.6 : 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {resendStatus === 'sent' ? '✓ Sent!' : resendStatus === 'sending' ? 'Sending...' : 'Resend'}
                      </button>
                    </div>
                    {resendStatus === 'sent' && (
                      <p style={{ color: '#059669', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                        Check your inbox for the new verification link.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <PublicFooter />
    </div>
  );
};

export default VerifyEmail;
